import { Link } from "react-router-dom";
import {
  ArrowRight,
  CircleAlert,
  MapPin,
  MapPinOff,
  Radar,
  Search,
} from "lucide-react";
import type { Partner } from "@/context/PartnerContext";
import ServiceBadge from "@/components/ui-custom/ServiceBadge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";

const formatNumber = new Intl.NumberFormat("el-GR").format;

interface NetworkPartnerListProps {
  partners: Partner[];
  selectedPartnerId: string | null;
  onSelect: (partnerId: string) => void;
  onResetFilters: () => void;
}

export function NetworkPartnerList({
  partners,
  selectedPartnerId,
  onSelect,
  onResetFilters,
}: NetworkPartnerListProps) {
  return (
    <Card className="flex min-h-0 flex-col overflow-hidden rounded-2xl border-slate-200 shadow-sm lg:h-[720px]">
      <CardHeader className="border-b border-slate-100 bg-slate-50/70 p-4 sm:p-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle className="text-base text-slate-950">Σημεία δικτύου</CardTitle>
            <p className="mt-1 text-xs text-slate-500">{formatNumber(partners.length)} συνεργάτες στα τρέχοντα φίλτρα</p>
          </div>
          <Badge variant="outline" className="border-sky-200 bg-sky-50 text-sky-800">Χάρτης</Badge>
        </div>
      </CardHeader>

      <CardContent className="min-h-0 flex-1 p-0">
        {partners.length === 0 ? (
          <div className="flex min-h-72 flex-col items-center justify-center px-6 text-center lg:h-full">
            <Search className="h-8 w-8 text-slate-300" />
            <p className="mt-4 font-semibold text-slate-800">Δεν βρέθηκαν σημεία</p>
            <p className="mt-1 max-w-xs text-sm leading-6 text-slate-500">Αλλάξτε αναζήτηση, κατάσταση ή υπηρεσία.</p>
            <Button type="button" variant="outline" className="mt-4 h-11" onClick={onResetFilters}>Καθαρισμός φίλτρων</Button>
          </div>
        ) : (
          <ScrollArea className="h-[520px] lg:h-full">
            <div className="space-y-2 p-3 sm:p-4">
              {partners.map((partner) => {
                const selected = selectedPartnerId === partner.id;
                const hasCoordinates = Number.isFinite(partner.latitude) && Number.isFinite(partner.longitude);
                const services = partner.services.slice(0, 2);

                return (
                  <div
                    key={partner.id}
                    className={`rounded-xl border p-3 transition ${selected ? "border-sky-300 bg-sky-50 shadow-sm" : "border-slate-200 bg-white hover:border-sky-200 hover:bg-slate-50"}`}
                  >
                    <button
                      type="button"
                      className="min-h-11 w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-600 focus-visible:ring-offset-2 disabled:cursor-not-allowed"
                      onClick={() => onSelect(partner.id)}
                      disabled={!hasCoordinates}
                      aria-pressed={selected}
                      aria-label={hasCoordinates ? `Εστίαση στον χάρτη: ${partner.name}` : `${partner.name}: χωρίς συντεταγμένες`}
                    >
                      <div className="flex min-w-0 items-start gap-3">
                        <div className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${!partner.active ? "bg-slate-100 text-slate-500" : partner.routingReady ? "bg-sky-950 text-white" : "bg-amber-100 text-amber-800"}`}>
                          {hasCoordinates ? <MapPin className="h-4 w-4" /> : <MapPinOff className="h-4 w-4" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <p className={`truncate text-sm font-semibold ${selected ? "text-sky-950" : "text-slate-900"}`}>{partner.name}</p>
                            {partner.active && partner.routingReady ? <Radar className="h-4 w-4 shrink-0 text-emerald-600" /> : partner.active ? <CircleAlert className="h-4 w-4 shrink-0 text-amber-600" /> : null}
                          </div>
                          <p className="mt-1 truncate text-xs text-slate-500">{partner.location || "Χωρίς καταχωρημένη περιοχή"}</p>
                        </div>
                      </div>
                    </button>

                    <div className="mt-3 flex flex-wrap gap-1.5">
                      <Badge variant="outline" className={partner.active ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-slate-200 bg-slate-100 text-slate-600"}>{partner.active ? "Ενεργός" : "Ανενεργός"}</Badge>
                      {services.map((service) => <ServiceBadge key={service} service={service} />)}
                      {partner.services.length > services.length && <Badge variant="outline" className="border-slate-200 text-slate-500">+{partner.services.length - services.length}</Badge>}
                    </div>

                    <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs">
                      <div className="rounded-lg bg-slate-50 px-2 py-2"><p className="font-bold tabular-nums text-slate-900">{formatNumber(partner.leadCount || 0)}</p><p className="mt-0.5 text-[10px] uppercase tracking-wide text-slate-400">Leads</p></div>
                      <div className="rounded-lg bg-sky-50 px-2 py-2"><p className="font-bold tabular-nums text-sky-800">{formatNumber(partner.leads30 || 0)}</p><p className="mt-0.5 text-[10px] uppercase tracking-wide text-sky-600">30 ημέρες</p></div>
                      <div className="rounded-lg bg-slate-50 px-2 py-2"><p className="font-bold tabular-nums text-slate-900">{partner.radius || 0} km</p><p className="mt-0.5 text-[10px] uppercase tracking-wide text-slate-400">Ακτίνα</p></div>
                    </div>

                    <Button asChild variant="ghost" className="mt-2 h-11 w-full justify-between px-2 text-slate-600 hover:bg-sky-50 hover:text-sky-800">
                      <Link to={`/admin/partners/${partner.id}/dashboard`}>Άνοιγμα συνεργάτη <ArrowRight className="h-4 w-4" /></Link>
                    </Button>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}
